import React, { useEffect, useMemo, useState } from 'react'
import { Form, Input as InputAntd } from 'antd'
import dp from 'dot-prop-immutable'
import { InputProps } from 'antd/lib/input'
import { FormItemProps } from 'antd/lib/form'

import { useT } from 'src/core/hooks/use-translation'

type Props = InputProps &
  Pick<FormItemProps, 'label' | 'required'> & {
    dataTestId: string
    decimalSeparator?: string
    formikProps: any
    precision?: number
    thousandSeparator?: string
  }

function formatValue(
  value: number,
  precision: number,
  decimalSeparator: string,
  thousandSeparator: string
): string {
  if (Number.isNaN(value)) {
    return ''
  }

  const fixed = value.toString()

  const [integer, decimal] = fixed.split('.')

  const integerFormatted = integer.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandSeparator
  )

  if (!decimal) {
    return `${integerFormatted},00`
  }

  return `${integerFormatted}${decimalSeparator}${decimal}`
}

function parseValue(
  value: string,
  decimalSeparator: string,
  thousandSeparator: string
): number {
  if (!value) {
    return 0
  }

  const normalized = value
    .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
    .replace(decimalSeparator, '.')
    .replace(/[^\d.]/g, '')

  const parsed = Number(normalized)

  return Number.isNaN(parsed) ? 0 : parsed
}

function InputCurrencyFreeDecimals({
  dataTestId = 'input-currency',
  decimalSeparator = ',',
  disabled = false,
  formikProps,
  label,
  name,
  placeholder,
  precision = 2,
  required = true,
  size = 'large',
  thousandSeparator = '.'
}: Props): JSX.Element {
  const { t } = useT()

  const [isFocused, setIsFocused] = useState(false)
  const [displayValue, setDisplayValue] = useState('')

  const error = useMemo(() => {
    return dp.get(formikProps.errors, name)
  }, [formikProps.errors, name])

  const touched = useMemo(() => {
    return dp.get(formikProps.touched, name)
  }, [formikProps.touched, name])

  const formikValue = dp.get(formikProps.values, name)

  useEffect(() => {
    if (isFocused) {
      return
    }

    if (
      formikValue === undefined ||
      formikValue === null ||
      formikValue === ''
    ) {
      setDisplayValue(`0${decimalSeparator}${'0'.repeat(precision)}`)

      return
    }

    setDisplayValue(
      formatValue(
        Number(formikValue),
        precision,
        decimalSeparator,
        thousandSeparator
      )
    )
  }, [decimalSeparator, formikValue, isFocused, precision, thousandSeparator])

  function handleFocus() {
    setIsFocused(true)

    const currentValue =
      formikValue === undefined || formikValue === null
        ? ''
        : String(formikValue).replace('.', decimalSeparator)

    setDisplayValue(currentValue)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    let value = e.target.value

    const regex = new RegExp(`[^\\d\\${decimalSeparator}]`, 'g')

    value = value.replace(regex, '')

    const occurrences = value.split(decimalSeparator).length - 1

    if (occurrences > 1) {
      return
    }

    setDisplayValue(value)
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const parsed = parseValue(displayValue, decimalSeparator, thousandSeparator)

    formikProps.setFieldValue(name, parsed)

    setDisplayValue(
      formatValue(parsed, precision, decimalSeparator, thousandSeparator)
    )

    setIsFocused(false)

    formikProps.handleBlur(e)
  }

  return (
    <Form.Item
      colon={false}
      hasFeedback
      help={touched && typeof error === 'string' ? t(error) : ''}
      label={t(label)}
      required={required}
      validateStatus={
        error && touched ? 'error' : !error && touched ? 'success' : ''
      }
    >
      <InputAntd
        allowClear={!required}
        data-test-id={dataTestId}
        disabled={disabled}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={t(placeholder)}
        size={size}
        value={displayValue}
      />
    </Form.Item>
  )
}

export default InputCurrencyFreeDecimals
